import { Dispatch, SetStateAction, useEffect, useRef } from 'react'
import { deepEqual } from 'fast-equals'

import { FilterConfigProps } from '../../Filter/Filter'
import { StationObjProps } from '../../../page/Map'
import { AppStateType } from '../../../../type/AppStateType'
import { PositionType } from '../../../../type/PositionType'
import { MapType } from '../../../../type/MapType'
import { fetchGolemio } from '../../../../utils/fetchGolemio'
import { sortStationsByDistanceFromPosition } from '../../../../utils/sortStationsByDistanceFromPosition'
import { filterStationsByTrashType } from '../../../../utils/filterStationsByTrashType'
import { formatBulkyWaste } from '../utils'

interface UseFetchDataProps {
    setStations: (stations: StationObjProps['stations']) => void
    setShowSpinner: Dispatch<SetStateAction<boolean>>
    setState: (state: AppStateType) => void
    position: PositionType
    filterConfig: FilterConfigProps
    map: google.maps.Map | undefined
    zoom: number
    type: MapType | undefined
}

const wasteCollectionYardsICZ = [
    'CZA00789',
    'CZA01223',
    'CZA00954',
    'CZA00005',
    'CZA00063',
    'CZA00767',
    'CZA01674',
    'CZA00017',
    'CZA00188',
    'CZA00400',
    'CZA00752',
    'CZA00230',
    'CZA00392',
    'CZA00578',
    'CZA00411',
    'CZA00547',
    'CZA00439',
    'CZA00522',
    'CZA01030',
    'CZA00955',
]

export const useFetchData = ({
    setStations,
    setState,
    setShowSpinner,
    position,
    filterConfig,
    map,
    // zoom,
    type,
}: UseFetchDataProps) => {
    const currentStates = useRef<{
        position: undefined | google.maps.LatLngLiteral
        filterConfig: FilterConfigProps
    }>({
        position: undefined,
        filterConfig: { trashTypes: [], onlyMonitored: false },
    })

    useEffect(() => {
        if (type === 'circular') return
        // check if position or filterConfig changed
        if (
            currentStates.current.position !== position ||
            !deepEqual(
                currentStates.current.filterConfig.trashTypes,
                filterConfig.trashTypes
            ) ||
            currentStates.current.filterConfig.onlyMonitored !==
                filterConfig.onlyMonitored
        ) {
            const positionChanged = currentStates.current.position !== position

            // If we have position and type, we know we will be fetching data
            if (position !== undefined && type !== undefined) {
                // Show spinner explicitly to indicate we are loading (either Google API or Data)
                setShowSpinner(true)

                // Wait for map to be fully loaded before fetching.
                // map being defined guarantees the entire Google Maps API (incl. geometry) is ready.
                // Do NOT update currentStates here — so outer condition re-fires correctly when map arrives.
                if (!map) {
                    return
                }

                // Safe to update now — map and geometry API are ready
                currentStates.current = { position, filterConfig }

                if (positionChanged) {
                    map?.setCenter(position)
                    // map?.setZoom(zoom)
                }

                const controller = new AbortController()

                fetchGolemio(type, `latlng=${position.lat}%2C${position.lng}`, {
                    signal: controller.signal,
                })
                    .then(async (r) => {
                        // console.log('returned response: ', r)
                        // check if a returned object has a features property and base on that set the yards state
                        if (
                            r &&
                            Object.prototype.hasOwnProperty.call(r, 'features')
                        ) {
                            // console.log('returned features: ', r.features)
                            // sort stations by distance from position
                            let finalStations =
                                sortStationsByDistanceFromPosition(
                                    position,
                                    r.features,
                                    type
                                )
                            if (type === 'bulky-waste') {
                                finalStations = await formatBulkyWaste(
                                    finalStations
                                )
                            }
                            if (type === 'waste-collection') {
                                finalStations = finalStations.filter(
                                    (station) => {
                                        if (!station.properties.icz) {
                                            return false
                                        }
                                        return wasteCollectionYardsICZ.includes(
                                            station.properties.icz.toUpperCase()
                                        )
                                    }
                                )
                            }
                            // filter sorted-waste stations by trash type if filter is set
                            if (filterConfig.trashTypes.length > 0) {
                                finalStations = filterStationsByTrashType(
                                    finalStations,
                                    filterConfig.trashTypes,
                                    type
                                )
                            }
                            // filter stations by monitored if filter is set
                            if (filterConfig.onlyMonitored) {
                                finalStations = finalStations.filter(
                                    (station) => station.properties.is_monitored
                                )
                            }
                            setStations(finalStations)
                        } else {
                            setStations([])
                        }
                        setState('init')
                    })
                    .catch((e) => {
                        // Ignore abort errors
                        if (e && e.name === 'AbortError') return
                        setStations([])
                        setState('error')
                    })
                    .finally(() => {
                        setShowSpinner(false)
                    })

                return () => {
                    controller.abort()
                }
            }
        }
    }, [
        position,
        `${filterConfig.trashTypes}`,
        filterConfig.onlyMonitored,
        type,
        map, // Listen for map object instance itself
    ])
}
